import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostBinding, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { FieldArrayType, FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { RktButtonDirective } from '@rocketfinancialcorp/rocket-ui/button';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';

import { AddressesByType, LinxFormAddressModel } from '@shared/models';

import { primaryIndex } from '../address-primary';
import { addressSummaryLines } from './address-summary';
import { ReusableAddress, addressParts, isBlankAddress, reusableAddresses } from './reusable-addresses';

// the picker's last option, which opens the empty fields instead
const NEW_ADDRESS = 'new-address';

/**
 * formly array type for the addresses of one type (KOR-15208, Figma 8280:8012): a saved address shows as a read-only
 * card with Edit and Delete, an open one as the form with Cancel and Save. Keeps rocket-ui repeat-section's class names
 * so the address block styles in styles.scss apply to both. Open/saved is view state only: the model, and so the
 * payload and the form's validity, are the same either way.
 */
@Component({
  selector: 'app-address-repeat-section',
  templateUrl: 'address-repeat-section.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FormsModule, FormlyModule, NgSelectModule, RktButtonDirective, RktIconComponent],
})
export class AddressRepeatSectionComponent extends FieldArrayType implements OnInit {
  // the block spacing styles.scss gives rocket-ui's repeat section; "unstyled" keeps its theme off
  @HostBinding('class') hostClass = 'rkt-form-repeat-section unstyled';

  private host: ElementRef<HTMLElement> = inject(ElementRef);

  private ref = inject(ChangeDetectorRef);

  private savedItems = new WeakSet<FormlyFieldConfig>();

  // the values an address had when Edit opened it, for Cancel
  private valuesBeforeEdit = new WeakMap<FormlyFieldConfig, LinxFormAddressModel>();

  // addresses whose "Set as primary" box is checked in the open form; the flag moves on Save
  private pendingPrimary = new WeakSet<FormlyFieldConfig>();

  // the reuse picker (phase 6): what an address copied, which chose "Enter a new address", which were saved unchosen
  private picked = new WeakMap<FormlyFieldConfig, string>();

  private enteringNew = new WeakSet<FormlyFieldConfig>();

  private pickerTouched = new WeakSet<FormlyFieldConfig>();

  private pickerOptionsCache = new WeakMap<FormlyFieldConfig, ReusableAddress[]>();

  readonly newAddressKey = NEW_ADDRESS;

  get header(): string {
    return this.props['header'] ?? '';
  }

  ngOnInit(): void {
    // A prefilled address opens as a card unless it is missing a required part, so the customer sees what to fix
    (this.field.fieldGroup ?? [])
      .filter((item) => item.model?.addressLine1 && !item.formControl?.invalid)
      .forEach((item) => this.savedItems.add(item));
  }

  isSaved(item: FormlyFieldConfig): boolean {
    return this.savedItems.has(item);
  }

  // Every type shown needs an address: Delete only while another remains, and no Cancel on a first one never saved
  canDelete(): boolean {
    return this.itemCount() > 1 || !this.props['notAllowEmpty'];
  }

  canCancel(item: FormlyFieldConfig, index: number): boolean {
    return this.valuesBeforeEdit.has(item) || index > 0;
  }

  isPrimary(index: number): boolean {
    return index === primaryIndex(this.model);
  }

  // "Primary" only once there is another address to tell it from (Figma note on 8280:7627)
  isPrimaryShown(index: number): boolean {
    return this.itemCount() > 1 && this.isPrimary(index);
  }

  isPrimaryChecked(item: FormlyFieldConfig, index: number): boolean {
    return this.isPrimary(index) || this.pendingPrimary.has(item);
  }

  togglePrimary(item: FormlyFieldConfig, checked: boolean): void {
    if (checked) this.pendingPrimary.add(item);
    else this.pendingPrimary.delete(item);
  }

  // A new address starts from the picker while another type has one to copy; editing a saved address never does
  showPicker(item: FormlyFieldConfig): boolean {
    if (this.isSaved(item) || this.valuesBeforeEdit.has(item) || this.enteringNew.has(item)) return false;
    return this.picked.has(item) || (isBlankAddress(item.model) && this.reusable().length > 0);
  }

  // The same array while the addresses on offer are unchanged, so ng-select is not handed new items every check
  pickerOptions(item: FormlyFieldConfig): ReusableAddress[] {
    const options = [
      ...this.reusable(),
      { key: NEW_ADDRESS, line1: 'Enter a new address', line2: '', address: {} as LinxFormAddressModel },
    ];
    const cached = this.pickerOptionsCache.get(item);
    if (cached?.map(({ key }) => key).join() === options.map(({ key }) => key).join()) return cached;
    this.pickerOptionsCache.set(item, options);
    return options;
  }

  pickedKey(item: FormlyFieldConfig): string | undefined {
    return this.picked.get(item);
  }

  // After the block's Save, or once Continue has touched the address (its fields are hidden behind the picker)
  hasPickerError(item: FormlyFieldConfig): boolean {
    return (this.pickerTouched.has(item) || !!item.formControl?.touched) && !this.picked.has(item);
  }

  pickerId(index: number): string {
    return `${this.id}-picker-${index}`;
  }

  pick(item: FormlyFieldConfig, option: ReusableAddress): void {
    if (option.key === NEW_ADDRESS) {
      this.enteringNew.add(item);
      if (this.picked.delete(item)) this.setAddressParts(item, addressParts());
      this.focusFirstField(item);
      return;
    }
    this.setAddressParts(item, addressParts(option.address));
    this.picked.set(item, option.key);
  }

  lines(item: FormlyFieldConfig): string[] {
    return addressSummaryLines(item.model ?? {}, { withContactName: !!this.props['withContactName'] });
  }

  actionLabel(action: 'Edit' | 'Delete', item: FormlyFieldConfig): string {
    return `${action} ${this.header.toLowerCase()} ${item.model?.addressLine1 ?? ''}`.trim();
  }

  addAddress(): void {
    // an object, not formly's default empty slot, so a copy or the primary flag written into it lands in the model
    this.add(this.itemCount(), {});
    this.focusFirstField(this.item(this.itemCount() - 1));
  }

  edit(index: number): void {
    const item = this.item(index);
    this.valuesBeforeEdit.set(item, { ...item.model });
    this.savedItems.delete(item);
    this.focusFirstField(item);
  }

  save(index: number): void {
    const item = this.item(index);
    if (item.formControl?.invalid) {
      if (this.showPicker(item) && !this.picked.has(item)) {
        this.pickerTouched.add(item);
        this.focusAfterRender(() => this.itemElement(index)?.querySelector<HTMLElement>('.address-picker input'));
        return;
      }
      // a copied address the fields reject (say, a state code) opens them, so the error shows where it can be fixed
      if (this.picked.delete(item)) this.enteringNew.add(item);
      item.formControl.markAllAsTouched();
      this.focusAfterRender(() => this.itemElement(index)?.querySelector<HTMLElement>('input.ng-invalid'));
      return;
    }
    this.close(index);
  }

  cancel(index: number): void {
    const item = this.item(index);
    this.pendingPrimary.delete(item);
    const values = this.valuesBeforeEdit.get(item);
    if (!values) {
      this.delete(index);
      return;
    }
    // Every control is set, so a part the customer typed into a blank field is cleared again
    const controls = Object.keys((item.formControl as { controls?: object })?.controls ?? {});
    item.formControl?.patchValue(Object.fromEntries(controls.map((key) => [key, values[key as keyof LinxFormAddressModel]])));
    this.close(index);
  }

  delete(index: number): void {
    this.remove(index);
    this.focusAfterRender(() => this.host.nativeElement.querySelector<HTMLElement>('.repeat-section-add-btn'));
  }

  private close(index: number): void {
    const item = this.item(index);
    if (this.pendingPrimary.delete(item)) this.makePrimary(index);
    this.valuesBeforeEdit.delete(item);
    this.picked.delete(item);
    this.enteringNew.delete(item);
    this.savedItems.add(item);
    this.focusAfterRender(() => this.itemElement(index)?.querySelector<HTMLElement>('.address-card-edit'));
  }

  // One primary per type: the flag moves, and formly is told the model changed so the step gets the new payload
  private makePrimary(index: number): void {
    (this.model ?? []).forEach((address: LinxFormAddressModel | undefined, i: number) => {
      if (address) address.primary = i === index;
    });
    this.notifyModelChange();
  }

  // The picker hides the fields, and formly copies a control into the model only through its rendered field: the model
  // and the controls are set together
  private setAddressParts(item: FormlyFieldConfig, parts: Partial<LinxFormAddressModel>): void {
    const index = this.field.fieldGroup!.indexOf(item);
    // an empty slot gets its object here, so the copy cannot land in a throwaway one and leave a hole in the payload
    this.model[index] ??= {};
    Object.assign(this.model[index], parts);
    item.formControl?.patchValue(parts);
    this.notifyModelChange();
  }

  // formly emits the form's modelChange on this, which is how the step hears of a change made outside a field
  private notifyModelChange(): void {
    this.options.fieldChanges?.next({ field: this.field, value: this.model, type: 'valueChanges' });
  }

  // the step's other address types sit beside this one in the parent model
  private reusable(): ReusableAddress[] {
    return reusableAddresses((this.field.parent?.model ?? {}) as AddressesByType, this.field.key as keyof AddressesByType);
  }

  private item(index: number): FormlyFieldConfig {
    return this.field.fieldGroup![index];
  }

  private itemCount(): number {
    return this.field.fieldGroup?.length ?? 0;
  }

  private itemElement(index: number): HTMLElement | undefined {
    return this.host.nativeElement.querySelectorAll<HTMLElement>(':scope > .repeat-section-item-wrapper')[index];
  }

  // formly focuses a field once its input exists (formlyAttributes), which is after this view renders. A row the
  // address type hides (the contact names, outside shipping) and a disabled field are skipped.
  private focusFirstField(item: FormlyFieldConfig): void {
    const fields = (item.fieldGroup ?? [])
      .filter(
        (row) =>
          !String(row.className ?? '')
            .split(' ')
            .includes('hidden'),
      )
      .flatMap((row) => row.fieldGroup ?? []);
    const first = fields.find((field) => field.key && !field.props?.disabled);
    if (first) first.focus = true;
    this.ref.detectChanges();
  }

  // Renders the change first, so the element to focus (a card's Edit, the add link) exists
  private focusAfterRender(target: () => HTMLElement | null | undefined): void {
    this.ref.detectChanges();
    target()?.focus();
  }
}
